// Who can do what in the admin dashboard. One module so the nav, the route
// guards and the tRPC procedures can never disagree about a role's reach —
// a mismatch here is a privilege bug, not a cosmetic one.
//
// Roles are plain strings on `user.role` (better-auth `admin` plugin), and
// better-auth's own `adminRoles` stays ["admin"] — only that role may manage
// users. These constants govern *our* dashboard, not better-auth internals.

export const ROLES = [
	"commercial",
	"community_manager",
	"designer",
	"admin",
] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
	commercial: "تجاري",
	community_manager: "مسؤول المجتمع",
	designer: "مصمّم",
	admin: "مدير",
};

export const ROLE_HINTS: Record<Role, string> = {
	commercial: "يتابع الطلبات ويغيّر حالاتها",
	community_manager: "يسجّل الطلبات نيابة عن الزبائن ويطّلع على طلباته فقط",
	designer:
		"يطّلع على البطاقات التقنية وقوائم المتخرّجين، ويرفع التصاميم والشهادات — لا يرى الطلبات",
	admin: "صلاحيات كاملة",
};

/** Anything not recognised is treated as the least-privileged role. */
export function asRole(value: string | null | undefined): Role {
	return (ROLES as readonly string[]).includes(value ?? "")
		? (value as Role)
		: "commercial";
}

export function isAdmin(role: string | null | undefined) {
	return asRole(role) === "admin";
}

/**
 * The community manager exists to key in leads for people who call in and
 * can't register themselves. They may also open the orders page to check on
 * what they registered, but strictly read-only and scoped to their own leads
 * — see `leadsReadProcedure` for the server-side enforcement. They must never
 * see the full pipeline, other people's leads, the dashboard, or who created
 * what.
 */
export function isCommunityManager(role: string | null | undefined) {
	return asRole(role) === "community_manager";
}

/**
 * The designer turns each training's technical data sheet into its social
 * media design (see `canEditFicheDesign`) and has no business anywhere else:
 * no leads, no customer data, no schedule. Every "all roles" gate below
 * names them explicitly, so adding a role can never widen access by default.
 */
export function isDesigner(role: string | null | undefined) {
	return asRole(role) === "designer";
}

/**
 * May open the full leads pipeline: every lead, the dashboard, exports, bulk
 * actions. An allowlist on purpose — written as "everyone but the community
 * manager" it would have handed the pipeline to every role added since.
 */
export function canViewLeads(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial";
}

/**
 * May open the orders page at all. Every role but the designer — a community
 * manager gets the same route but a read-only view scoped to their own leads
 * (handled in the page component + `leadsReadProcedure`), not a separate page.
 */
export function canOpenOrdersPage(role: string | null | undefined) {
	return !isDesigner(role);
}

/** May key a lead in on someone's behalf — every role but the designer. */
export function canRegisterLeads(role: string | null | undefined) {
	return !isDesigner(role);
}

/**
 * May see who created a lead and whether it came from the public page.
 * Admin-only by product decision: it is oversight data, not sales data.
 */
export function canViewLeadOrigin(role: string | null | undefined) {
	return isAdmin(role);
}

/**
 * May delete an order outright. Admin-only, and deliberately narrower than
 * every other lead write: a delete takes the row and its whole audit trail
 * (`LeadStatusEvent` cascades) with it, so a commercial who mis-clicks would
 * destroy the history their own pipeline is judged on. Sales close leads by
 * moving them to a lost status instead — `leads.remove` refuses them
 * server-side and the button is hidden client-side.
 */
export function canDeleteLeads(role: string | null | undefined) {
	return isAdmin(role);
}

/**
 * May open the trainings area — at least its editions tab (the schedule) and
 * each edition's overview. Every role but the designer; what else they see
 * there is gated by the functions below.
 */
export function canViewEditions(role: string | null | undefined) {
	return !isDesigner(role);
}

/**
 * May create, edit or delete editions — the courses on the schedule. Admin and
 * commercial (sales) only; every other role sees them read-only — the write
 * buttons are hidden client-side and `requireEditionManager` refuses the
 * mutations server-side.
 */
export function canManageEditions(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial";
}

/**
 * May build, publish and read feedback questionnaires, and delete responses.
 * Admin and commercial (sales) — the same pair as scheduling: sales run the
 * cohorts, so they are the ones who need the evaluation form out the door and
 * the answers back. The community manager stays out: questionnaire responses
 * carry respondent names, emails and phones for trainings they had no part in.
 */
export function canManageQuestionnaires(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial";
}

/**
 * May see the trainings themselves — the trainings tab, each training's page —
 * and each edition's marketing report. Admin and commercial (sales): sales
 * judge a cohort by its campaign figures, but only read them — every write is
 * gated by `canManageTrainings`.
 */
export function canViewTrainings(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial";
}

/**
 * May create or edit a training and fill in its marketing report. Admin-only;
 * sales get the same pages read-only — the write buttons are hidden
 * client-side and `requireAdmin` refuses the mutations server-side.
 */
export function canManageTrainings(role: string | null | undefined) {
	return isAdmin(role);
}

/**
 * May open an edition's cost report — "التكاليف والتسعير". Admin and commercial:
 * it is the run's money, so the community manager stays out.
 */
export function canViewCostReports(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial";
}

/**
 * May fill in and edit the cost report. Sales only, by product decision — the
 * mirror of the marketing report, which only an admin writes. Admins read it;
 * `requireCostReportManager` refuses their writes server-side.
 */
export function canManageCostReports(role: string | null | undefined) {
	return asRole(role) === "commercial";
}

/**
 * May open the weekly B2C report — orders, contacts, paid registrations, CA
 * and money collected over a week. Admin and commercial: it is pipeline-wide
 * sales data, which the community manager never sees.
 */
export function canViewWeeklyReport(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial";
}

/**
 * May open the technical data sheets page — each training's fiche technique
 * and the social media design made from it. Admin, sales and the designer.
 */
export function canViewFiches(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial" || isDesigner(role);
}

/**
 * May upload or remove a training's technical data sheet, write the brief for
 * the designer, and send a delivered design back for changes. Admin and sales;
 * the designer reads the sheet but never edits it.
 */
export function canEditFicheSheet(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial";
}

/**
 * May upload or remove the social media design and move the request through
 * "in progress" to "delivered". Admin and the designer; sales see the design
 * but never edit it. `fiches.*` and the fiche upload route enforce both.
 */
export function canEditFicheDesign(role: string | null | undefined) {
	return isAdmin(role) || isDesigner(role);
}

/**
 * May open the certificates page — each edition's list of students and the
 * certificates made for them. Admin, sales and the designer, like the fiches.
 */
export function canViewDegrees(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial" || isDesigner(role);
}

/**
 * May write an edition's student list (names as printed, birth date and
 * place), the brief for the designer, and send delivered certificates back for
 * changes. Admin and sales; the designer reads the list but never edits it.
 */
export function canEditDegreeStudents(role: string | null | undefined) {
	return isAdmin(role) || asRole(role) === "commercial";
}

/**
 * May upload or remove certificates, set the shared Drive link, and move the
 * request through "in progress" to "delivered". Admin and the designer; sales
 * see the certificates but never edit them. `degrees.*` and the degree upload
 * route enforce both.
 */
export function canEditDegreeFiles(role: string | null | undefined) {
	return isAdmin(role) || isDesigner(role);
}

/** Where a role lands after login, and where it is bounced back to. */
export function homeRouteFor(role: string | null | undefined) {
	if (isCommunityManager(role)) return "/admin/register-lead";
	if (isDesigner(role)) return "/admin/fiches";
	return "/admin";
}
